import { type FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { BsFileEarmarkPlus, BsTrash, BsCaretDownFill, BsFolderPlus } from 'react-icons/bs';
import { Draggable } from 'react-beautiful-dnd';
import DelayedDroppableWrapper from '@editor2/components/DelayedDroppableWrapper';

import { type RootState } from '@editor2/reducers/root';
import AddPageModal from '@editor2/components/AddPageModal';
import AddPageGroupModal from '@editor2/components/AddPageGroupModal';
import AddChapterModal from '@editor2/components/AddChapterModal';
import { deleteChapter, editChaptersCollapseState } from '@editor2/reducers/editor/editorSlice';
import { CollapseState } from '@editor2/types/collapseState';

import TableOfContentsPageGroup from './TocPageGroup';
import TableOfContentsPage from './TocPage';
import { chapterContainer } from '@editor2/containers/TableOfContentsPage/styles';

interface Props {
	chapterId: string;
	index: number;
	courseId: number;
}

const TableOfContentsChapter: FC<Props> = (props) => {
	const { chapterId, index, courseId } = props;

	const dispatch = useDispatch();
	const { chapters, course, collapseState, config } = useSelector(
		(state: RootState) => state.courses[courseId]
	);
	const chapter = chapters.entities[chapterId];

	const [isAddPageModalShown, setAddPageModalShown] = useState(false);
	const [isAddPageGroupModalShown, setAddPageGroupModalShown] = useState(false);
	const [isAddChapterModalShown, setAddChapterModalShown] = useState(false);
	const [collapseChapter, setCollapseChapter] = useState(false);

	useEffect(() => {
		if (collapseState.chapters === CollapseState.CollapseAll) {
			setCollapseChapter(true);
		}
		if (collapseState.chapters === CollapseState.ExpandAll) {
			setCollapseChapter(false);
		}
	}, [collapseState.chapters]);

	const pageGroupsEnabled = () => {
		const type = config.chapter_types[chapter.chapter_type] ? chapter.chapter_type : 'normal';

		return config.chapter_types[type].page_groups_enabled;
	};

	const onCollapseChapterClick = () => {
		dispatch(editChaptersCollapseState({ collapseState: CollapseState.Mixed, courseId }));
		setCollapseChapter(!collapseChapter);
	};

	const onDeleteChapterClick = () => {
		if (window.confirm(`Are you sure to delete the "${chapter.name}" chapter?`)) {
			dispatch(deleteChapter({ chapterId, courseId }));
		}
	};

	const pageGroups = (
		<>
			<DelayedDroppableWrapper droppableId={chapterId} type="PageGroup">
				{(provided) => (
					<div ref={provided.innerRef} {...provided.droppableProps}>
						{chapter.page_groups &&
							chapter.page_groups.map((pageGroupId, index) => (
								<TableOfContentsPageGroup
									pageGroupId={pageGroupId}
									chapterId={chapterId}
									courseId={courseId}
									index={index}
									key={pageGroupId}
								/>
							))}
						{provided.placeholder}
					</div>
				)}
			</DelayedDroppableWrapper>

			<Button
				variant="light"
				size="sm"
				className="addPageGroupButton"
				onClick={() => setAddPageGroupModalShown(true)}>
				<BsFolderPlus />
				Add page group
			</Button>

			{isAddPageGroupModalShown && (
				<AddPageGroupModal
					chapterId={chapterId}
					onHide={() => setAddPageGroupModalShown(false)}
					isShown={isAddPageGroupModalShown}
					courseId={courseId}
				/>
			)}
		</>
	);

	const pages = (
		<>
			<DelayedDroppableWrapper droppableId={chapterId} type="Page">
				{(provided) => (
					<div ref={provided.innerRef} {...provided.droppableProps}>
						{chapter.pages &&
							chapter.pages.map((pageId, index) => (
								<TableOfContentsPage
									pageId={pageId}
									chapterId={chapterId}
									courseId={courseId}
									index={index}
									key={pageId}
								/>
							))}
						{provided.placeholder}
					</div>
				)}
			</DelayedDroppableWrapper>

			<Button
				variant="light"
				size="sm"
				className="addPageButton"
				onClick={() => setAddPageModalShown(true)}>
				<BsFileEarmarkPlus />
				Add page
			</Button>

			{isAddPageModalShown && (
				<AddPageModal
					chapterId={chapterId}
					onHide={() => setAddPageModalShown(false)}
					isShown={isAddPageModalShown}
					courseId={courseId}
				/>
			)}
		</>
	);

	const chapterHeader = (
		<h2 className="chapterHeader">
			<div className="chapterMenu">
				<BsFolderPlus onClick={() => setAddChapterModalShown(true)} />
				<BsTrash onClick={() => onDeleteChapterClick()} />
			</div>

			<div className="chapterName">
				{chapter.chapter_number && chapter.chapter_number !== ' ' && (
					<span className="chapterNumber">{chapter.chapter_number}.</span>
				)}
				<Link to={`/courses/${course.id}/chapters/${chapter.family_id}`}>{chapter.name}</Link>
			</div>

			<div>
				<BsCaretDownFill
					className={collapseChapter ? 'collapseIconRotate' : 'collapseIcon'}
					onClick={onCollapseChapterClick}
				/>
			</div>
		</h2>
	);

	return (
		<Draggable draggableId={chapterId} index={index}>
			{(provided) => (
				<div
					className={chapterContainer}
					ref={provided.innerRef}
					{...provided.draggableProps}
					{...provided.dragHandleProps}>
					{chapterHeader}

					<div className={`pageGroupsContainer ${collapseChapter ? 'listHidden' : 'listShown'}`}>
						{pageGroupsEnabled() ? pageGroups : pages}
					</div>

					{isAddChapterModalShown && (
						<AddChapterModal
							chapterId={chapterId}
							onHide={() => setAddChapterModalShown(false)}
							isShown={isAddChapterModalShown}
							courseId={courseId}
						/>
					)}
				</div>
			)}
		</Draggable>
	);
};

export default TableOfContentsChapter;
